import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Save } from "lucide-react";
import { api, errMsg, today } from "@/lib/api";
import { PageHeader, Field, Panel } from "@/components/Common";
import { LineRow } from "@/pages/company/InvoiceEditor";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateField } from "@/components/DateField";

const emptyLine = () => ({ descrizione: "", quantita: 1, unita: "", prezzo: 0, sconto: 0, aliquota: 22, natura: "" });
const CAUSALI = ["Vendita", "Conto visione", "Conto lavorazione", "Reso", "Omaggio", "Riparazione", "Altro"];

const Opt = ({ value, onChange, items, testid, placeholder }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{items.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
  </Select>
);

const Trasporto = ({ f, set }) => {
  const inp = (k, label, type = "text") => <Field label={label}><Input data-testid={`ddt-${k}-input`} type={type} value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} /></Field>;
  return (
    <Panel title="Dati del trasporto">
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Causale del trasporto"><Opt testid="ddt-causale-select" value={f.causale_trasporto} onChange={(v) => set("causale_trasporto", v)} items={CAUSALI.map((c) => [c, c])} /></Field>
        <Field label="Trasporto a cura"><Opt testid="ddt-cura-select" value={f.trasporto_a_cura} onChange={(v) => set("trasporto_a_cura", v)} items={["Mittente", "Destinatario", "Vettore"].map((c) => [c, c])} /></Field>
        <Field label="Porto"><Opt testid="ddt-porto-select" value={f.porto} onChange={(v) => set("porto", v)} items={[["Franco", "Franco"], ["Assegnato", "Assegnato"]]} /></Field>
        {inp("vettore", "Vettore (se a cura vettore)")}
        {inp("aspetto_beni", "Aspetto esteriore dei beni")}
        {inp("data_trasporto", "Inizio trasporto", "datetime-local")}
        {inp("colli", "N. colli", "number")}
        {inp("peso", "Peso (es. 12 kg)")}
        <Field label="Luogo di destinazione (se diverso)"><Input data-testid="ddt-destinazione-input" value={f.destinazione} onChange={(e) => set("destinazione", e.target.value)} /></Field>
        <Field label="Note" className="sm:col-span-3"><Textarea data-testid="ddt-note-input" rows={2} value={f.note} onChange={(e) => set("note", e.target.value)} /></Field>
      </div>
    </Panel>
  );
};

export default function DdtEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ data: today(), customer_id: "", lines: [emptyLine()], destinazione: "", causale_trasporto: "Vendita",
    trasporto_a_cura: "Mittente", vettore: "", aspetto_beni: "", colli: 0, peso: "", porto: "Franco", data_trasporto: "", note: "" });
  useEffect(() => {
    api.get("/customers", { params: { tipo: "cliente" } }).then((r) => setCustomers(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    if (id) api.get(`/ddt/${id}`).then((r) => setF(r.data));
  }, [id]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const setLine = (i, ln) => setF((x) => ({ ...x, lines: x.lines.map((l, j) => (j === i ? ln : l)) }));

  const save = async () => {
    if (!f.customer_id) return toast.error("Seleziona il destinatario");
    if (f.lines.some((l) => !l.descrizione)) return toast.error("Ogni riga deve avere una descrizione");
    setBusy(true);
    try {
      const body = { ...f, colli: Number(f.colli || 0), lines: f.lines.map((l) => ({ ...l, quantita: Number(l.quantita), prezzo: Number(l.prezzo || 0), sconto: Number(l.sconto || 0) })) };
      const { data } = id ? await api.put(`/ddt/${id}`, body) : await api.post("/ddt", body);
      toast.success(`DDT ${data.numero} salvato`);
      nav("/app/ddt");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title={id ? `Modifica DDT ${f.numero || ""}` : "Nuovo documento di trasporto"} subtitle="Il numero viene assegnato automaticamente. I prezzi servono quando il DDT viene inserito in fattura.">
        <Button className="bg-sky-600 hover:bg-sky-700" disabled={busy} onClick={save} data-testid="save-ddt-button"><Save className="mr-2 h-4 w-4" />Salva DDT</Button>
      </PageHeader>
      <Panel title="Intestazione">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Data"><DateField testid="ddt-date-input" value={f.data} onChange={(v) => set("data", v)} /></Field>
          <Field label="Destinatario"><Opt testid="ddt-customer-select" placeholder="Seleziona…" value={f.customer_id} onChange={(v) => set("customer_id", v)} items={customers.map((c) => [c.id, c.denominazione || `${c.nome} ${c.cognome}`])} /></Field>
        </div>
      </Panel>
      <Panel title="Beni trasportati" action={<Button size="sm" variant="outline" data-testid="ddt-add-line-button" onClick={() => set("lines", [...f.lines, emptyLine()])}><Plus className="mr-1 h-4 w-4" />Riga</Button>}>
        {f.lines.map((ln, i) => (
          <LineRow key={i} ln={ln} i={i} products={products} onChange={setLine} onRemove={(j) => set("lines", f.lines.length > 1 ? f.lines.filter((_, k) => k !== j) : f.lines)} />
        ))}
      </Panel>
      <Trasporto f={f} set={set} />
    </div>
  );
}
